import { Download, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { convertISODateToMMDDYYYY, convertISOToTime } from '@/lib/utils';
import { BillingAmount } from '@/components/BillingAmount';
import KeyValueListDisplay from '@/components/KeyValueDisplay';
import type { HIInvoiceResponse } from '@/types/homeInspection.types';
import { EmailStatus } from '@/types/types';
import axiosInstance from '@/services/axiosInstance';
import { EmailLogStatusLabel, invoiceStatusBadgeDetails } from '@/utils/constant';
import { InvoiceStatusBadge } from '@/pages/caseManagement/invoice/InvoiceView';
import { toast } from '@/components/ui/use-toast';
import { buildHiInvoicePath } from './invoice/hiInvoiceRoute';

const downloadHIInvoicePDF = async (invoiceId: string, invoiceNumber: string) => {
  try {
    const response = await axiosInstance.get(`/home-inspection/invoice/${invoiceId}/pdf`, {
      responseType: 'blob',
    });
    const url = URL.createObjectURL(response.data as Blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${invoiceNumber}.pdf`;
    link.click();
    URL.revokeObjectURL(url);
  } catch {
    toast({
      variant: 'destructive',
      description: 'Failed to download invoice PDF.',
    });
  }
};

type InvoiceCardProps = {
  invoice: HIInvoiceResponse;
  interactive: boolean;
  onOpen?: () => void;
};

const HIInvoiceCard = ({ invoice, interactive, onOpen }: InvoiceCardProps) => {
  const status = invoice.status ?? 'DRAFT';

  return (
    <div
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      className={`flex flex-row gap-2 justify-between p-4 rounded-lg bg-[#F5F5F5]${
        interactive ? ' cursor-pointer' : ''
      }`}
      onClick={interactive ? onOpen : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.target !== e.currentTarget) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onOpen?.();
              }
            }
          : undefined
      }
    >
      <div className="flex flex-row gap-2">
        <FileText className="w-6 h-6 text-primary" />
        <div className="flex flex-col gap-2">
          <span className="font-medium text-sm text-primary">{invoice.invoiceNumber}</span>
          <span className="text-xs text-placeholder">
            {`Updated: ${convertISODateToMMDDYYYY(invoice.updatedAt)} ${convertISOToTime(invoice.updatedAt)}`}
          </span>
          <div className="font-medium">
            <span className="text-placeholder">Amount: </span>
            <BillingAmount value={invoice.totalAmount} className="text-primary" />
          </div>
        </div>
      </div>
      <div className="flex flex-col justify-between gap-2">
        <InvoiceStatusBadge key={status} status={invoiceStatusBadgeDetails[status]} />
        {interactive && (
          <div className="flex items-end justify-end">
            <button
              type="button"
              aria-label={`Download invoice ${invoice.invoiceNumber} PDF`}
              className="p-0 border-0 bg-transparent"
              onClick={async (e) => {
                e.stopPropagation();
                await downloadHIInvoicePDF(invoice.id, invoice.invoiceNumber);
              }}
            >
              <Download className="h-5 w-5 text-primary mr-4 cursor-pointer" aria-hidden />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

type Props = {
  invoice: HIInvoiceResponse | undefined;
  workOrderId: string | undefined;
  workOrderNumber: string | undefined;
};

export const HIWorkOrderSidePanelBilling = ({ invoice, workOrderId, workOrderNumber }: Props) => {
  const navigate = useNavigate();
  const canOpenInvoice = Boolean(invoice && workOrderId);
  const emailLogs = [...(invoice?.emailLogs ?? [])]
    .sort((a, b) => new Date(b.sentAt ?? 0).getTime() - new Date(a.sentAt ?? 0).getTime())
    .slice(0, 5);

  const openInvoice = () => {
    if (!invoice || !workOrderId) return;
    navigate(buildHiInvoicePath(invoice.invoiceNumber, workOrderId, workOrderNumber));
  };

  return (
    <div className="flex flex-col gap-6 w-full px-6 py-6 text-sm">
      {emailLogs.length > 0 && (
        <div className="flex flex-col gap-3 w-full">
          <span className="text-primary font-semibold">Emails Sent</span>
          <KeyValueListDisplay
            data={emailLogs.map((log) => ({
              label: log.sentAt ? convertISODateToMMDDYYYY(log.sentAt) + ' ' + convertISOToTime(log.sentAt) : '-',
              value: EmailLogStatusLabel[log.status],
              valueClassName: log.status === EmailStatus.SENT ? 'text-green-700' : 'text-error',
            }))}
          />
        </div>
      )}

      {invoice && canOpenInvoice ? (
        <HIInvoiceCard invoice={invoice} interactive onOpen={openInvoice} />
      ) : invoice ? (
        <HIInvoiceCard invoice={invoice} interactive={false} />
      ) : (
        <div className="w-full justify-center flex font-medium text-sm">No Billing Data</div>
      )}
    </div>
  );
};
